import { DiscordIcon } from "@/components/DiscordIcon";
import { GchatIcon } from "@/components/GchatIcon";
import { GithubIcon } from "@/components/GithubIcon";
import { GitlabIcon } from "@/components/GitlabIcon";
import { Microsoft365Icon } from "@/components/MicrosoftIcons";
import { ProviderLogo } from "@/components/ProviderLogo";
import { SlackIcon } from "@/components/SlackIcon";
import { TeamsIcon } from "@/components/TeamsIcon";
import { api, type ContainerRow } from "@/lib/api";
import { useCodeHosts } from "@/lib/code-host";
import { booChatStatus, booCodeHostStatus, type ConnectionStatus } from "@/lib/connection-status";
import { useFeatureFlag } from "@/lib/feature-flag";
import { useQuery } from "@tanstack/react-query";
import { createElement, type ComponentType } from "react";

// A Boo's Connections: what boo-backend connects for it directly, as opposed to
// the integrations hub's catalog (the Integrations tab). Laid out like Devin's
// Settings → Connections: grouped rows on the Boo's home (ConnectionsTab), each
// opening its own page at …/containers/$containerId/connections/<id>. The
// workspace connects shared ones once (the org's Connections page) and each Boo uses
// what it's given, plus connections of its own. Non-component module so the
// home, the pages, the router, and the catalog can share it.

export type ConnectionId =
  | "slack"
  | "microsoft-teams"
  | "google-chat"
  | "discord"
  | "github"
  | "gitlab"
  | "microsoft-365"
  | "google-workspace";

export type ConnectionGroup = "chat" | "code" | "work";

export interface ConnectionDef {
  id: ConnectionId;
  group: ConnectionGroup;
  name: string;
  // One line under the name on the home and the page.
  description: string;
  Logo: ComponentType<{ className?: string }>;
}

const GoogleLogo = ({ className }: { className?: string }) =>
  createElement(ProviderLogo, { upstream: "google", className });

export const CONNECTIONS: ReadonlyArray<ConnectionDef> = [
  { id: "slack", group: "chat", name: "Slack", description: "DMs and @mentions in your Slack workspace.", Logo: SlackIcon },
  {
    id: "microsoft-teams",
    group: "chat",
    name: "Microsoft Teams",
    description: "DMs and @mentions in your Teams tenant.",
    Logo: TeamsIcon,
  },
  {
    id: "google-chat",
    group: "chat",
    name: "Google Chat",
    description: "DMs and @mentions in your Google Workspace.",
    Logo: GchatIcon,
  },
  { id: "discord", group: "chat", name: "Discord", description: "DMs and @mentions in your server.", Logo: DiscordIcon },
  {
    id: "github",
    group: "code",
    name: "GitHub",
    description: "Repositories Boo works in, through the Boo GitHub App.",
    Logo: GithubIcon,
  },
  { id: "gitlab", group: "code", name: "GitLab", description: "Projects Boo works in, on gitlab.com or your own instance.", Logo: GitlabIcon },
  {
    id: "microsoft-365",
    group: "work",
    name: "Microsoft 365",
    description: "Files, Teams history, mail, and calendar, as a Microsoft account Boo signs in as.",
    Logo: Microsoft365Icon,
  },
  {
    id: "google-workspace",
    group: "work",
    name: "Google Workspace",
    description: "Gmail, Drive, Calendar, and more, through your own OAuth client.",
    Logo: GoogleLogo,
  },
];

export const CONNECTION_GROUPS: ReadonlyArray<{ id: ConnectionGroup; title: string; description: (boo: string) => string }> = [
  { id: "chat", title: "Chat", description: (boo) => `Where your team talks to ${boo}.` },
  { id: "code", title: "Code", description: (boo) => `Where ${boo} works in code. New apps it builds go to the default.` },
  { id: "work", title: "Work apps", description: (boo) => `Accounts ${boo} signs in to as itself.` },
];

export function findConnection(id: string | undefined): ConnectionDef | undefined {
  return CONNECTIONS.find((c) => c.id === id);
}

// The hub lists some connections in its catalog (`connectedBy: "app"`); their
// tiles open the connection's page.
const HUB_SLUGS: Partial<Record<string, ConnectionId>> = { microsoft_365: "microsoft-365" };
export function connectionForHubSlug(slug: string): ConnectionDef | undefined {
  return findConnection(HUB_SLUGS[slug]);
}

export const ms365StatusKey = (containerId: string) => ["ms365-status", containerId];

const connected = (label: string): ConnectionStatus => ({ loading: false, state: "connected", label });

// Every connection's status on this Boo. A connection missing from the map
// isn't offered here: chief of staff Boos are Slack-only, Discord is behind a
// flag, and Google Workspace shows only where it was set up.
export function useConnectionStatuses(container: ContainerRow): Partial<Record<ConnectionId, ConnectionStatus>> {
  const id = container.id;
  const running = container.deploymentStatus === "running";
  const chiefOfStaff = container.posture === "chief-of-staff";
  const discordEnabled = useFeatureFlag("discord-integration");
  const gchatBooApp = useFeatureFlag("gchat-boo-app");

  // Same keys and fetchers as the connection pages, so they share one cache.
  const slackBoo = useQuery({ queryKey: ["slack-boo-status"], queryFn: () => api.slack.boo.status() });
  const slackCustom = useQuery({
    queryKey: ["slack", id],
    queryFn: () => api.slack.custom.status(id, true),
    enabled: running,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
  const teams = useQuery({ queryKey: ["teams-boo-status"], queryFn: () => api.teams.boo.status(), enabled: !chiefOfStaff });
  const gchatBoo = useQuery({
    queryKey: ["gchat-boo-status"],
    queryFn: () => api.gchat.boo.status(),
    enabled: gchatBooApp && !chiefOfStaff,
  });
  const gchatCustom = useQuery({
    queryKey: ["gchat-custom-status", id],
    queryFn: () => api.gchat.custom.status(id),
    enabled: running && !chiefOfStaff,
  });
  const discord = useQuery({
    queryKey: ["discord-boo-status"],
    queryFn: () => api.discord.boo.status(),
    enabled: discordEnabled && !chiefOfStaff,
  });
  const ms365 = useQuery({ queryKey: ms365StatusKey(id), queryFn: () => api.ms365.status(id) });
  const workspace = useQuery({
    queryKey: ["gchat-workspace-admin-state", id],
    queryFn: () => api.gchat.custom.admin.state(id),
    enabled: running,
  });
  const codeHosts = useCodeHosts(id);

  const out: Partial<Record<ConnectionId, ConnectionStatus>> = {};

  out.slack = slackCustom.data?.connected
    ? connected(`Your custom app in ${slackCustom.data.teamName ?? "your workspace"}`)
    : booChatStatus(slackBoo.data, slackBoo.isLoading, slackBoo.data?.teamName, id);

  if (!chiefOfStaff) {
    out["microsoft-teams"] = booChatStatus(teams.data, teams.isLoading, teams.data?.tenantName, id);
    out["google-chat"] = gchatCustom.data?.connected
      ? connected(`Your custom app in ${gchatCustom.data.customerName ?? "your workspace"}`)
      : booChatStatus(gchatBoo.data, gchatBooApp ? gchatBoo.isLoading : gchatCustom.isLoading, gchatBoo.data?.customerName, id);
    if (discordEnabled) out.discord = booChatStatus(discord.data, discord.isLoading, discord.data?.guildName, id);
  }

  for (const host of ["github", "gitlab"] as const) {
    out[host] = booCodeHostStatus(
      codeHosts.active.filter((c) => c.host === host),
      codeHosts.data?.defaultConnectionId,
      codeHosts.query.isLoading,
    );
  }

  out["microsoft-365"] = ms365.data?.connected
    ? connected(`Connected as ${ms365.data.account ?? "the Boo account"}`)
    : { loading: ms365.isLoading, state: "off", label: "Not connected" };

  if (workspace.data && (workspace.data.connected || workspace.data.clientConfigured)) {
    out["google-workspace"] = workspace.data.connected
      ? connected(`Connected as ${workspace.data.connectedAs ?? "your account"}`)
      : { loading: false, state: "off", label: "Not authorized" };
  }

  return out;
}
